import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ToastAction, type ToastActionElement } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import {
    getPatientPrescriptions, getQueueMy, queueCompleteConsultation, queueReopenConsultation, queueStartConsultation,
    type QueueEntry,
} from "@/lib/api";

interface Props {
    patientId: string;
    /** Unsaved prescription: it is saved before the consultation ends */
    hasUnsavedChanges?: boolean;
    /** Saves the prescription, then runs the queue action (only called with unsaved changes) */
    onSaveFirst?: (then: () => void) => void;
    /** Told whether Next / End consultation is showing, so the footer can make it the main action */
    onMainShown?: (shown: boolean) => void;
    className?: string;
}

const QUEUE_CHANGED = "queue-changed";
/** The doctor's home screen opens the print preview of the prescription in this event's detail */
export const PRINT_PRESCRIPTION = "print-prescription";

/**
 * The doctor's queue button for the patient on screen: End consultation. The front desk sees the patient
 * as done straight away, and the doctor lands on the home screen, where the next patient is called.
 * Shown only when this patient is in the doctor's queue today.
 */
export default function ConsultationControls({ patientId, hasUnsavedChanges = false, onSaveFirst, onMainShown, className }: Props) {
    const navigate = useNavigate();
    const [entries, setEntries] = useState<QueueEntry[] | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(() => {
        getQueueMy().then(q => setEntries(q?.entries ?? [])).catch(() => setEntries([]));
    }, []);
    useEffect(() => {
        load();
        const timer = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30_000);
        window.addEventListener("focus", load);
        // The pad's copy and the header's copy of these buttons: a change from one refreshes the other
        window.addEventListener(QUEUE_CHANGED, load);
        return () => { clearInterval(timer); window.removeEventListener("focus", load); window.removeEventListener(QUEUE_CHANGED, load); };
    }, [load]);

    const entry = entries?.find(e => e.patientId === patientId && ["waiting", "in_consultation", "on_hold"].includes(e.status));
    // On the prescription pad, writing is the consultation: a patient opened while still waiting gets the
    // same button, and is started on the server first
    const consulting = !!entry && (entry.status === "in_consultation" || (entry.status === "waiting" && !!onSaveFirst));
    useEffect(() => { onMainShown?.(consulting); }, [consulting, onMainShown]);
    if (!entry) return null;

    const run = async (key: string, action: () => Promise<unknown>, done: string, goHome = false) => {
        setBusy(key);
        try {
            await action();
            window.dispatchEvent(new Event(QUEUE_CHANGED));
            if (goHome) { navigate("/"); await toastEnded(); }
            else { toast({ title: done }); load(); }
        } catch (err: any) {
            toast({ title: "Queue not updated", description: err?.message ?? "Please try again", variant: "destructive" });
        } finally {
            setBusy(null);
        }
    };
    // "Consultation ended" with Print Rx (the prescription saved on this visit, for a doctor who didn't print)
    // and Undo (a mis-click on the main button: back in consultation, on this patient's page)
    const toastEnded = async () => {
        const rx = entry.visitId
            ? (await getPatientPrescriptions(patientId).catch(() => [])).find(p => p.visit_id === entry.visitId)
            : undefined;
        const undo = async () => {
            try {
                await queueReopenConsultation(entry.id);
                window.dispatchEvent(new Event(QUEUE_CHANGED));
                navigate(`/patients/${patientId}`);
                toast({ title: "Consultation reopened", description: entry.patientName });
            } catch (err: any) {
                toast({ title: "Couldn't undo", description: err?.message ?? "Please try again", variant: "destructive" });
            }
        };
        toast({
            title: "Consultation ended",
            description: entry.patientName,
            duration: 10000,
            action: (
                <div className="flex gap-1.5">
                    {rx && (
                        <ToastAction altText="Print prescription" onClick={() => window.dispatchEvent(new CustomEvent(PRINT_PRESCRIPTION, { detail: rx }))}>
                            Print Rx
                        </ToastAction>
                    )}
                    <ToastAction altText="Undo end consultation" onClick={undo}>Undo</ToastAction>
                </div>
            ) as unknown as ToastActionElement,
        });
    };
    // An unsaved prescription is saved first; if the save fails, nothing in the queue changes
    const withSave = (go: () => void) => (hasUnsavedChanges && onSaveFirst ? onSaveFirst(go) : go());
    const blocked = hasUnsavedChanges && !onSaveFirst;
    const startFirst = async () => { if (entry.status === "waiting") await queueStartConsultation(entry.id); };

    // Page-level actions (patient banner, prescription footer): 36px / 14px like the buttons beside them
    const btn = "inline-flex items-center gap-1 h-9 touch:h-10 px-3 rounded-md text-sm font-medium transition-colors disabled:opacity-50";
    // The accent, as every main button: green is kept for done / paid
    const main = cn(btn, "bg-primary text-primary-foreground hover:bg-primary/90");
    const spinner = <Loader2 className="w-3.5 h-3.5 animate-spin" />;

    return (
        <div className={cn("flex items-center gap-1.5", className)}>
            {!consulting && (entry.status === "waiting" || entry.status === "on_hold") && (
                <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => run("start", () => queueStartConsultation(entry.id), `Consultation started · token ${entry.tokenNumber}`)}
                    className={cn(btn, "border border-border text-foreground hover:bg-muted")}
                >
                    {busy === "start" ? spinner : entry.status === "on_hold" ? "Resume" : "Start consultation"}
                </button>
            )}

            {consulting && (
                <button
                    type="button"
                    disabled={!!busy || blocked}
                    title={blocked ? "Save the prescription first" : undefined}
                    onClick={() => withSave(() => run("end", async () => { await startFirst(); await queueCompleteConsultation(entry.id); },
                        "Consultation ended", true))}
                    className={main}
                >
                    {busy === "end" ? spinner : "End consultation"}
                </button>
            )}
        </div>
    );
}
